import BaseComponent from "../core/base-component.js";

enum ToggleMode {
    SetRestore,
    SetRemove
}

interface AttributeTogglerConfig {
    target?: string;
    attribute: string;
    toggleValue: string;
    toggleMode?: ToggleMode;
    remove?: boolean;
    storeState?: boolean;
    storeId?: string;
    parent?: string;
    initialState?: boolean;
    aria?: string;
}

const DEFAULT_ELEMENT_SELECTOR: string = '[data-th-toggle="attribute"]';

const defaultOptions: AttributeTogglerConfig = {
    target: '',
    attribute: 'class',
    toggleValue: 'show',
    toggleMode: 0,
    storeState: false,
    storeId: '',
    initialState: false,
    parent: '',
    aria: ''
};

class AttributeToggler extends BaseComponent {
    protected options: AttributeTogglerConfig = defaultOptions;
    protected isLoaded: boolean = false;
    protected targetElement: HTMLElement|null;
    protected attributeInitialValue: string = '';
    protected currentState: number = 0;

    public override init() {
        this.options = this.assignConfig<AttributeTogglerConfig>(defaultOptions, this.config);
        let targetSelector = (this.options.target !== '') ? this.options.target : this.element.getAttribute('data-th-target') ?? null;
        if(targetSelector === null && (this.element.getAttribute('href') ?? '') !== '#') {
            targetSelector = this.element.getAttribute('href');
        }
        if(targetSelector && targetSelector !== '') {
            let parentEl: Document|HTMLElement = document;
            if(
                this.options.parent
                && this.options.parent !== ''
                && document.querySelector(this.options.parent)
            ) {
                parentEl = document.querySelector<HTMLElement>(this.options.parent)!;
            }
            this.targetElement = parentEl.querySelector<HTMLElement>(targetSelector);
        }
    }

    public override process() {
        if(this.targetElement) {
            if(!this.targetElement.hasAttribute(this.options.attribute)) {
                this.targetElement.setAttribute(this.options.attribute, '');
            }
            this.attributeInitialValue = this.targetElement.getAttribute(this.options.attribute) ?? '';

            const ariaName = this.options.aria;
            if(ariaName !== '') {
                if(!this.element.hasAttribute('aria-' + ariaName)) {
                    this.element.setAttribute('aria-' + ariaName, 'false');
                }
            }

            this.element.removeAttribute('href');
            this.element.addEventListener('click', (ev: MouseEvent) => {
                this.toggle();
            })

            if(this.options.initialState && this.options.initialState === true) {
                this.currentState = 1;
            }

            if(this.options.storeState && this.options.storeId && this.options.storeId !== '' && window.localStorage.getItem(this.options.storeId)) {
                this.currentState = parseInt(window.localStorage.getItem(this.options.storeId) ?? '0');
            }

            if(this.currentState > 0) {
                this.show()
            } else {
                this.hide()
            }
        }
    }

    public show() {
        this.toggle(true);
    }

    public hide() {
        this.toggle(false);
    }

    private toggle(force?: boolean) {
        if(this.targetElement == null) {
            return;
        }
        const toggleValue = this.options.toggleValue;
        const toggleMode = this.options.toggleMode ?? 1;
        const attrName = this.options.attribute;
        const ariaName = this.options.aria;

        let ariaState = false;

        if(attrName === 'class') {
            ariaState = true;
            toggleValue.split(' ').forEach((val) => {
                this.targetElement!.classList.toggle(val, force);
                if(!this.targetElement!.classList.contains(val)) {
                    ariaState = false;
                }
            });
        } else {
            if(typeof(force) !== null) {
                if(force) {
                    this.targetElement.setAttribute(attrName, toggleValue);
                    ariaState = true;
                } else {
                    if(toggleMode > 0) {
                        this.targetElement.removeAttribute(attrName);
                    } else {
                        this.targetElement.setAttribute(attrName, this.attributeInitialValue);
                    }
                }
            } else {
                if(toggleMode > 0) {
                    if(this.targetElement.hasAttribute(attrName)) {
                        this.targetElement.removeAttribute(attrName);
                    } else {
                        this.targetElement.setAttribute(attrName, toggleValue);
                        ariaState = true;
                    }
                } else {
                    if(this.targetElement.getAttribute(attrName) === toggleValue) {
                        this.targetElement.setAttribute(attrName, this.attributeInitialValue);
                    } else {
                        this.targetElement.setAttribute(attrName, toggleValue);
                        ariaState = true;
                    }
                }
            }
        }

        if(this.element.hasAttribute('aria-' + ariaName)) {
            this.element.setAttribute('aria-' + ariaName, ((ariaState) ? 'true' : 'false'));
        }

        if(ariaState) {
            this.currentState = 1;
        } else {
            this.currentState = 0;
        }

        if(this.options.storeState && this.options.storeId && this.options.storeId !== '') {
            window.localStorage.setItem(this.options.storeId, this.currentState.toString());
        }

    }
}

// Observe
AttributeToggler.initializeDom(DEFAULT_ELEMENT_SELECTOR, false, false, true);

export type { AttributeTogglerConfig };
export default AttributeToggler;
